import type { Meal } from './meal';
import { PRICE_LEVELS, type PriceLevel } from './types';

export type Answer = 'yes' | 'no' | 'skip';

/**
 * Everything the questions have established so far.
 *
 * The 2016 version kept this in five module-level globals mutated from inside a
 * `switch (counter)`, which is how it ended up with dead states and a cuisine that
 * got assigned twice. Here it is one immutable value and every question is a pure
 * function over it.
 */
export interface Facts {
  priceLevels?: PriceLevel[];
  radiusM?: number;
  /** A single Table A type - the narrowest one the descent has reached so far. */
  includedType?: string;
  openNow?: boolean;
  /**
   * Which meal the run is for, set only when "Eating right now?" was answered yes.
   * Answering "later on" leaves it undefined, which is what makes the search cover
   * every time of day rather than the one that happens to be now.
   */
  meal?: Meal;
  sitDown?: boolean;
  /** Google's own floor on star rating. Undefined means "show me everything". */
  minRating?: number;
  /** Steers the text query rather than includedType - see mapping.ts for why. */
  vegetarian?: boolean;
  /**
   * Which axis the run led with, set by the routing question. Undefined means it hasn't
   * been asked yet, which is what gates both descents: neither tree is on offer until
   * the user has said whether they're after a dish or a kind of food.
   */
  lead?: 'cuisine' | 'dish';
  /**
   * Where the descent through the tradition tree has got to: the ids of the nodes
   * entered, root excluded. Empty means "still at the root". This is the whole gate
   * mechanism - a tree question is eligible when the path matches its parent exactly,
   * so at most one is ever on offer and dead states cannot exist.
   *
   * A dish-led run uses this too, for the tradition follow-up: its ids are rooted in
   * whichever `DISH_FOLLOWUPS` tree the chosen dish opened rather than in `CUISINE_TREE`.
   */
  cuisinePath: string[];
  /** The same, for the dish tree. */
  dishPath: string[];
  /** Set by "skip" on any tradition question: stop descending, keep the type so far. */
  cuisineAxisClosed: boolean;
  /** Set by "skip" on any dish question. Independent of the tradition axis, so
   *  "don't mind the cuisine, but I want noodles" is expressible. */
  dishAxisClosed: boolean;
  /** Set by "skip" on a price question. */
  priceAxisClosed: boolean;
  /** Set by "skip" on a distance question. */
  distanceAxisClosed: boolean;
}

export const ALL_PRICE_LEVELS: PriceLevel[] = [...PRICE_LEVELS];

export function initialFacts(): Facts {
  return {
    cuisinePath: [],
    dishPath: [],
    cuisineAxisClosed: false,
    dishAxisClosed: false,
    priceAxisClosed: false,
    distanceAxisClosed: false,
  };
}

/** Shallow-merge helper so questions read as declarations rather than mutations. */
export function withFacts(facts: Facts, patch: Partial<Facts>): Facts {
  return { ...facts, ...patch };
}

export const DEFAULT_RADIUS_M = 8000;

/** The radius actually sent, once "skip" or an unanswered distance axis is accounted for. */
export function effectiveRadius(facts: Facts): number {
  return facts.radiusM ?? DEFAULT_RADIUS_M;
}
